import CallRecordsAPI from 'dashboard/api/callRecords';

export const state = {
  records: [],
  stats: null,
  meta: {
    totalCount: 0,
    currentPage: 1,
    perPage: 25,
  },
  uiFlags: {
    isFetching: false,
    isFetchingStats: false,
    isDeleting: false,
  },
  // Pedido de "Atualizar do CDR" em aberto. status: idle | running | finished |
  // skipped | failed; `result` é o payload do evento ActionCable.
  reconcile: {
    status: 'idle',
    mode: null,
    requestedAt: null,
    result: null,
  },
  filters: {
    direction: '',
    disposition: '',
    userIds: [],
    teamId: '',
    inboxId: '',
    startDate: '',
    endDate: '',
    query: '',
    perPage: 25,
    dateRangeId: 'LAST_7_DAYS',
  },
};

export const getters = {
  getRecords: $state => $state.records,
  getStats: $state => $state.stats,
  getMeta: $state => $state.meta,
  getUIFlags: $state => $state.uiFlags,
  getReconcile: $state => $state.reconcile,
  getFilters: $state => $state.filters,
  getById: $state => id => $state.records.find(r => r.id === id),
  getAnswered: $state => $state.records.filter(r => r.answered),
  getMissed: $state => $state.records.filter(r => r.missed),
  getIncoming: $state => $state.records.filter(r => r.direction === 'incoming'),
  getOutgoing: $state => $state.records.filter(r => r.direction === 'outgoing'),
};

// Helper para verificar se o registro corresponde aos filtros
const doesRecordMatchFilters = (record, filters) => {
  if (filters.direction && record.direction !== filters.direction) {
    return false;
  }
  if (filters.disposition && record.disposition !== filters.disposition) {
    return false;
  }
  if (
    filters.userIds?.length > 0 &&
    !filters.userIds.includes(record.user_id)
  ) {
    return false;
  }
  if (filters.startDate && filters.endDate) {
    const callDate = new Date(record.call_date).getTime() / 1000;
    if (callDate < filters.startDate || callDate > filters.endDate) {
      return false;
    }
  }
  return true;
};

export const actions = {
  async fetch({ commit, state: $state }, { page = 1, resetList = false } = {}) {
    commit('SET_UI_FLAG', { isFetching: true });
    try {
      const directionArr = $state.filters.direction
        ? $state.filters.direction.split(',')
        : [];
      const dispositionArr = $state.filters.disposition
        ? $state.filters.disposition.split(',')
        : [];

      const params = {
        page,
        per_page: $state.filters.perPage || 25,
        direction: directionArr.length > 0 ? directionArr : undefined,
        user_ids:
          $state.filters.userIds?.length > 0
            ? $state.filters.userIds
            : undefined,
        team_id: $state.filters.teamId || undefined,
        inbox_id: $state.filters.inboxId || undefined,
        start_date: $state.filters.startDate || undefined,
        end_date: $state.filters.endDate || undefined,
        q: $state.filters.query || undefined,
        disposition: dispositionArr.length > 0 ? dispositionArr : undefined,
      };

      // Remove undefined values
      Object.keys(params).forEach(
        key => params[key] === undefined && delete params[key]
      );

      const response = await CallRecordsAPI.get(params);
      const { payload, meta } = response.data;

      if (resetList || page === 1) {
        commit('SET_RECORDS', payload);
      } else {
        commit('APPEND_RECORDS', payload);
      }
      commit('SET_META', meta);

      return payload;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[callRecords] Error fetching:', error);
      throw error;
    } finally {
      commit('SET_UI_FLAG', { isFetching: false });
    }
  },

  async fetchStats({ commit, state: $state }) {
    commit('SET_UI_FLAG', { isFetchingStats: true });
    try {
      const directionArr = $state.filters.direction
        ? $state.filters.direction.split(',')
        : [];
      const dispositionArr = $state.filters.disposition
        ? $state.filters.disposition.split(',')
        : [];

      const params = {
        start_date: $state.filters.startDate || undefined,
        end_date: $state.filters.endDate || undefined,
        direction: directionArr.length > 0 ? directionArr : undefined,
        disposition: dispositionArr.length > 0 ? dispositionArr : undefined,
        user_ids:
          $state.filters.userIds?.length > 0
            ? $state.filters.userIds
            : undefined,
        team_id: $state.filters.teamId || undefined,
      };

      // Remove undefined values
      Object.keys(params).forEach(
        key => params[key] === undefined && delete params[key]
      );

      const response = await CallRecordsAPI.getStats(params);
      commit('SET_STATS', response.data);
      return response.data;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[callRecords] Error fetching stats:', error);
      throw error;
    } finally {
      commit('SET_UI_FLAG', { isFetchingStats: false });
    }
  },

  async fetchForContact({ commit }, contactId) {
    commit('SET_UI_FLAG', { isFetching: true });
    try {
      const response = await CallRecordsAPI.getForContact(contactId);
      return response.data.payload;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[callRecords] Error fetching for contact:', error);
      throw error;
    } finally {
      commit('SET_UI_FLAG', { isFetching: false });
    }
  },

  async fetchForConversation({ commit }, conversationId) {
    commit('SET_UI_FLAG', { isFetching: true });
    try {
      const response = await CallRecordsAPI.getForConversation(conversationId);
      return response.data.payload;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[callRecords] Error fetching for conversation:', error);
      throw error;
    } finally {
      commit('SET_UI_FLAG', { isFetching: false });
    }
  },

  async delete({ commit }, id) {
    commit('SET_UI_FLAG', { isDeleting: true });
    try {
      await CallRecordsAPI.delete(id);
      commit('DELETE_RECORD', id);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[callRecords] Error deleting:', error);
      throw error;
    } finally {
      commit('SET_UI_FLAG', { isDeleting: false });
    }
  },

  async setFilters({ commit, dispatch }, filters) {
    commit('SET_FILTERS', filters);
    await Promise.all([
      dispatch('fetch', { page: 1, resetList: true }),
      dispatch('fetchStats'),
    ]);
  },

  clearFilters({ commit, dispatch }) {
    commit('CLEAR_FILTERS');
    return dispatch('fetch', { page: 1, resetList: true });
  },

  // Pede o reconcile do CDR ao backend, que só ENFILEIRA (202). Nada é
  // recarregado aqui: o job avisa o desfecho pelo ActionCable
  // (`call_records.reconcile.finished` → handleReconcileEvent), e só então lista
  // e stats são refeitas. Deixa o erro propagar (sem fallback) pro componente
  // exibir toast. Manda o período filtrado na tela: é o que faz o backend
  // reprocessar histórico antigo — sozinho, o job incremental nunca volta no
  // passado.
  async reconcile({ commit, state: $state }) {
    const startDate = $state.filters.startDate || undefined;
    const endDate = $state.filters.endDate || undefined;
    // Espelha a regra do job: com as duas datas é backfill, senão incremental.
    // Guardar o modo pedido é o que separa o nosso desfecho do incremental do
    // cron, que roda a cada 5min e emite o mesmo evento.
    const mode = startDate && endDate ? 'backfill' : 'incremental';
    await CallRecordsAPI.reconcile({
      start_date: startDate,
      end_date: endDate,
    });
    commit('SET_RECONCILE', {
      status: 'running',
      mode,
      requestedAt: Date.now(),
      result: null,
    });
  },

  // Desfecho vindo do Asterisk::CdrReconcileJob via ActionCable. Ignora evento
  // que não é do pedido em aberto (cron incremental, ou nada pendente).
  async handleReconcileEvent({ commit, dispatch, state: $state }, data) {
    const pending = $state.reconcile;
    if (pending.status !== 'running' || data.mode !== pending.mode) return;

    commit('SET_RECONCILE', {
      ...pending,
      status: data.event_type,
      result: data,
    });
    if (data.event_type === 'finished') {
      await Promise.all([
        dispatch('fetch', { page: 1, resetList: true }),
        dispatch('fetchStats'),
      ]);
    }
  },

  resetReconcile({ commit }) {
    commit('SET_RECONCILE', {
      status: 'idle',
      mode: null,
      requestedAt: null,
      result: null,
    });
  },

  async downloadCsv({ state: $state }) {
    const directionArr = $state.filters.direction
      ? $state.filters.direction.split(',')
      : [];
    const dispositionArr = $state.filters.disposition
      ? $state.filters.disposition.split(',')
      : [];

    const params = {
      direction: directionArr.length > 0 ? directionArr : undefined,
      disposition: dispositionArr.length > 0 ? dispositionArr : undefined,
      user_ids:
        $state.filters.userIds?.length > 0 ? $state.filters.userIds : undefined,
      team_id: $state.filters.teamId || undefined,
      start_date: $state.filters.startDate || undefined,
      end_date: $state.filters.endDate || undefined,
      q: $state.filters.query || undefined,
    };

    Object.keys(params).forEach(
      key => params[key] === undefined && delete params[key]
    );

    const response = await CallRecordsAPI.download(params);
    return response.data;
  },

  // Adiciona um novo registro via WebSocket
  addRecord({ commit, state: $state }, record) {
    // Verifica se o registro já existe
    const exists = $state.records.some(r => r.id === record.id);
    if (exists) return;

    // Verifica se o registro está dentro dos filtros atuais
    const matchesFilters = doesRecordMatchFilters(record, $state.filters);
    if (matchesFilters) {
      commit('ADD_RECORD', record);
    }
  },
};

export const mutations = {
  SET_UI_FLAG($state, data) {
    $state.uiFlags = { ...$state.uiFlags, ...data };
  },

  SET_RECONCILE($state, reconcile) {
    $state.reconcile = { ...reconcile };
  },

  SET_RECORDS($state, records) {
    $state.records = records;
  },

  APPEND_RECORDS($state, records) {
    $state.records = [...$state.records, ...records];
  },

  SET_META($state, meta) {
    $state.meta = {
      totalCount: meta.total_count,
      currentPage: meta.current_page,
      perPage: meta.per_page,
    };
  },

  SET_STATS($state, data) {
    $state.stats = data;
  },

  DELETE_RECORD($state, id) {
    $state.records = $state.records.filter(r => r.id !== id);
    if ($state.meta.totalCount > 0) {
      $state.meta.totalCount -= 1;
    }
  },

  ADD_RECORD($state, record) {
    // Adiciona no início da lista (mais recente primeiro)
    $state.records = [record, ...$state.records];
    $state.meta.totalCount += 1;
  },

  SET_FILTERS($state, filters) {
    $state.filters = { ...$state.filters, ...filters };
  },

  CLEAR_FILTERS($state) {
    $state.filters = {
      direction: '',
      disposition: '',
      userIds: [],
      teamId: '',
      inboxId: '',
      startDate: '',
      endDate: '',
      query: '',
      perPage: 25,
      dateRangeId: 'LAST_7_DAYS',
    };
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
